import {
  Box,
  BoxProps,
  css,
  ModalProps,
  Drawer as MuiDrawer,
  Stack,
  StackProps,
  Typography,
  TypographyProps
} from '@mui/material'
import Link from 'next/link'
import GroupeIcon from 'assets/branding/groupe.svg'
import LogoIcon from 'assets/branding/miles.svg'
import ChevronDown from 'assets/icons/chevron-down.svg'
import { Button, ButtonProps } from 'components/common/Button'
import {
  NewFlexContainer,
  NewFlexContainerProps
} from 'components/common/common.style'
import { unifiedColors } from 'styles/colors'
import { styled } from 'styles/styled'
import { themeUtils } from 'styles/theme'

export const HeaderContainer = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  gap: 24px;
  z-index: 101;
  ${themeUtils.maxWidth.md} {
    gap: 16px;
  }
`

export const LogoContainer = styled(Link)`
  min-width: 84px;
  ${themeUtils.maxWidth.lg} {
    min-width: 76px;
  }
`

export const Logo = styled(LogoIcon)`
  width: 84px;
  display: flex;
  justify-content: flex-start;
  color: black;
  cursor: pointer;
  grid-column: 1;
`

interface RaceTypeIconProps {
  isDisciplineOpen: boolean
}

const RaceTypeIcon = styled(ChevronDown, {
  shouldForwardProp: (prop) => prop !== 'isDisciplineOpen'
})<RaceTypeIconProps>`
  min-width: 10px;
  margin-left: 8px;
  transition: 100ms;
  transform: ${({ isDisciplineOpen }) =>
    isDisciplineOpen ? 'rotate(-90deg)' : 'rotate(0)'};
  & path {
    fill: ${unifiedColors.label.default};
  }
`

export const RaceTypeButton = styled(
  ({ isDisciplineOpen, ...props }: ButtonProps & RaceTypeIconProps) => (
    <Button
      size="medium"
      noBackground
      noBorder
      rightIcon={<RaceTypeIcon isDisciplineOpen={isDisciplineOpen} />}
      {...props}
    />
  )
)`
  ${themeUtils.hideMd}
  white-space: nowrap;
`

export const HomeHeaderContainer = styled((props: NewFlexContainerProps) => (
  <NewFlexContainer
    width="100%"
    alignItems="center"
    flexDirection="column"
    padding="52px 0"
    gap="40px"
    {...props}
  />
))`
  background-color: ${unifiedColors.surface.subdued};
  border-radius: 120px;
  ${themeUtils.maxWidth.md} {
    border-radius: 48px;
    padding: 40px 16px;
    gap: 32px;
  }
`

export const CatchPhraseContainer = styled((props: NewFlexContainerProps) => (
  <NewFlexContainer
    flexDirection="column"
    alignItems="center"
    gap="16px"
    {...props}
  />
))`
  ${themeUtils.maxWidth.md} {
    gap: 0;
  }
`

export const Groupe = styled(GroupeIcon)`
  ${themeUtils.maxWidth.md} {
    transform: scale(0.77);
  }
`

export const CatchPhrase = styled((props: TypographyProps) => (
  <Typography variant="h1" maxWidth={500} textAlign="center" {...props} />
))`
  ${themeUtils.maxWidth.md} {
    font-size: 34px;
    max-width: 300px;
  }
`

export const SearchBarContainer = styled(Box)`
  ${themeUtils.minWidth.md} {
    width: 100%;
  }
  ${themeUtils.maxWidth!.md} {
    width: 100%;
    grid-column: 1/3;
    grid-row: 2;
  }
`

export const ButtonsContainer = styled((props: StackProps) => (
  <Stack {...props} flexDirection="row" justifyContent="flex-end" gap={1.5} />
))`
  ${themeUtils.maxWidth.md} {
    grid-row: 1;
    grid-column: 2/3;
  }
`

export const minWidthIcon = css`
  min-width: 18px;
`

export const CartItemsCount = styled((props: BoxProps) => (
  <Box
    width={20}
    height={20}
    position="absolute"
    top={-5}
    right={-5}
    bgcolor={unifiedColors.surface.pink}
    borderRadius="8px"
    display="flex"
    alignItems="center"
    justifyContent="center"
    p={0.25}
    {...props}
  />
))``

export const CartItemsCountValue = styled((props: TypographyProps) => (
  <Typography
    variant="body2"
    fontWeight={700}
    color={unifiedColors.label.onPink}
    {...props}
  />
))``

export const Drawer = ({ children, ...props }: ModalProps) => (
  <MuiDrawer
    anchor="bottom"
    transitionDuration={300}
    sx={{
      '& .MuiDrawer-paper': {
        height: '100%',
        backgroundColor: unifiedColors.surface.subdued
      }
    }}
    {...props}
  >
    {children}
  </MuiDrawer>
)
